import axios from 'axios';

/**
 * Sube un archivo directo a S3 usando una URL prefirmada emitida por el backend.
 *
 * Existe porque la API corre en Lambda detrás de API Gateway: el body se
 * codifica en base64 (+33%) antes de invocar la función, y contra el límite de
 * 6 MB de invocación eso deja un techo real de ~4.4 MB por archivo. Cualquier
 * cosa más grande devuelve 413 sin llegar siquiera a Laravel. Subiendo directo
 * a S3, el request a la API lleva solo el key.
 *
 * Adaptado de uploadFileToS3 en Views/Cotizador/Steps/Step4/Authentication.jsx,
 * desacoplado del toast para poder reusarse desde cualquier paso.
 *
 * @param {File} file
 * @param {string} presignedUrl
 * @param {{contentType?: string, maxRetries?: number, timeout?: number, onProgress?: (pct: number) => void}} options
 * @returns {Promise<void>}
 */
export const uploadFileToS3 = async (file, presignedUrl, options = {}) => {
  const {
    // Conviene mandarlo explícito: para un mismo PDF, file.type puede venir
    // vacío o como application/octet-stream según el sistema del usuario, y el
    // backend valida el Content-Type con el que S3 haya guardado el objeto.
    contentType = file.type || 'application/octet-stream',
    maxRetries = 3,
    // 30s (lo que usa el cotizador) se queda corto para 10 MB en conexión lenta.
    timeout = 120000,
    onProgress,
  } = options;

  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt += 1) {
    try {
      await axios.put(presignedUrl, file, {
        headers: { 'Content-Type': contentType },
        timeout,
        onUploadProgress: onProgress
          ? (e) => onProgress(e.total ? Math.round((e.loaded * 100) / e.total) : 0)
          : undefined,
      });
      return;
    } catch (error) {
      lastError = error;

      // Una firma vencida o un key rechazado no se arreglan reintentando.
      const status = error?.response?.status;
      if (status === 403 || status === 400) break;
    }
  }

  const detail =
    lastError?.response?.statusText || lastError?.message || 'error desconocido';
  throw new Error(`No se pudo subir ${file.name}: ${detail}`);
};

export default uploadFileToS3;
